import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { openFile, byteSize, Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './image-file.reducer';

export const ImageFile = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const imageFileList = useAppSelector(state => state.imageFile.entities);
  const loading = useAppSelector(state => state.imageFile.loading);
  const totalItems = useAppSelector(state => state.imageFile.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="image-file-heading" data-cy="ImageFileHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.imageFile.home.title">Image Files</Translate>
        <div className="d-flex justify-content-end">
          <Button
            className="me-2"
            color="info"
            onClick={handleSyncList}
            disabled={loading}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.imageFile.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button
            component={Link}
            to="/image-file/new"
            variant="contained"
            color="primary"
            id="jh-create-entity"
            data-cy="entityCreateButton"
            startIcon={<FontAwesomeIcon icon="plus" />}
          >
            <Translate contentKey="jhipsterSampleApplicationApp.imageFile.home.createLabel">Create new Image File</Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {imageFileList && imageFileList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('title')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.title">Title</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('title')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('alt')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.alt">Alt</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('alt')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('filename')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.filename">Filename</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('filename')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.createdBy">Created By</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.destination">Destination</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.tour">Tour</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.tourCategory">Tour Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.place">Place</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.placeCategory">Place Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.tourExtraCategory">Tour Extra Category</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.tourExtra">Tour Extra</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.vehicle">Vehicle</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.imageFile.driver">Driver</Translate> <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {imageFileList.map((imageFile, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/image-file/${imageFile.id}`} variant="text" size="small">
                        {imageFile.id}
                      </Button>
                    </TableCell>
                    <TableCell>{imageFile.code}</TableCell>
                    <TableCell>{imageFile.title}</TableCell>
                    <TableCell>{imageFile.alt}</TableCell>
                    <TableCell>{imageFile.filename}</TableCell>
                    <TableCell>
                      {imageFile.createdDate ? (
                        <TextFormat type="date" value={imageFile.createdDate} format={APP_LOCAL_DATE_FORMAT} />
                      ) : null}
                    </TableCell>
                    <TableCell>{imageFile.createdBy ? imageFile.createdBy.login : ''}</TableCell>
                    <TableCell>
                      {imageFile.destination ? (
                        <Link to={`/destination/${imageFile.destination.id}`}>{imageFile.destination.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>{imageFile.tour ? <Link to={`/tour/${imageFile.tour.id}`}>{imageFile.tour.code}</Link> : ''}</TableCell>
                    <TableCell>
                      {imageFile.tourCategory ? (
                        <Link to={`/tour-category/${imageFile.tourCategory.id}`}>{imageFile.tourCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>{imageFile.place ? <Link to={`/place/${imageFile.place.id}`}>{imageFile.place.code}</Link> : ''}</TableCell>
                    <TableCell>
                      {imageFile.placeCategory ? (
                        <Link to={`/place-category/${imageFile.placeCategory.id}`}>{imageFile.placeCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      {imageFile.tourExtraCategory ? (
                        <Link to={`/tour-extra-category/${imageFile.tourExtraCategory.id}`}>{imageFile.tourExtraCategory.code}</Link>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      {imageFile.tourExtra ? <Link to={`/tour-extra/${imageFile.tourExtra.id}`}>{imageFile.tourExtra.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {imageFile.vehicle ? <Link to={`/vehicle/${imageFile.vehicle.id}`}>{imageFile.vehicle.plate}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {imageFile.driver ? <Link to={`/driver/${imageFile.driver.id}`}>{imageFile.driver.name}</Link> : ''}
                    </TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button
                          component={Link}
                          to={`/image-file/${imageFile.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                        >
                        </Button>
                        <Button
                          onClick={() =>
                            (window.location.href = `/image-file/${imageFile.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                          }
                          color="error"
                          startIcon={<FontAwesomeIcon icon="trash" />}
                        >
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.imageFile.home.notFound">No Image Files found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={imageFileList && imageFileList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ImageFile;
